import type { ComponentProps, FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { Database } from 'lucide-react';
import { z } from 'zod';

import { isType } from '@ballerine/common';

import type {
  RegistryInformationAdapterParamaters,
  RegistryInformationAdapterVendor,
} from '../RegistryDataDisplay/utils';
import { KyckrIcon } from '../../atoms/Icons/KyckrIcon';
import { OpenCorporatesIcon } from '../../atoms/Icons/OpenCorporatesIcon';
import { ReportWrapper } from '../../templates/report-shared/components/ReportWrapper/ReportWrapper';
import {
  KyckrRenderRaw,
  OpenCorporatesRenderRaw,
  RegistryDataDisplay,
} from '../RegistryDataDisplay';
import {
  registryInformationAdapter,
  RegistryInformationVendor,
} from '../RegistryDataDisplay/utils';

const VendorIcon = {
  [RegistryInformationVendor.KYCKR]: KyckrIcon,
  [RegistryInformationVendor.OPEN_CORPORATES]: OpenCorporatesIcon,
  [RegistryInformationVendor.ASIA_VERIFY]: () => null,
} as const satisfies Record<
  RegistryInformationAdapterVendor,
  FunctionComponent<{ className?: string }>
>;

export const VendorRenderRaw = {
  [RegistryInformationVendor.KYCKR]: KyckrRenderRaw,
  [RegistryInformationVendor.OPEN_CORPORATES]: OpenCorporatesRenderRaw,
  [RegistryInformationVendor.ASIA_VERIFY]: () => null,
} as const satisfies {
  [TKey in RegistryInformationAdapterVendor]: FunctionComponent<
    ComponentProps<
      Extract<ComponentProps<typeof RegistryDataDisplay<TKey>>, { raw: any }>['renderRaw']
    >
  >;
};

const ErrorRegistryInformationSchema = z.object({
  error: z.string().optional(),
  success: z.literal(false),
});

export const RegistryDataSection: FunctionComponent<{
  input: RegistryInformationAdapterParamaters;
}> = ({ input }) => {
  const adaptedData = useMemo(() => {
    if (!input.data) {
      return {
        processed: {},
        raw: input.data,
      };
    }

    return registryInformationAdapter(input);
  }, [input]);

  const Icon = useMemo(() => {
    return VendorIcon[input.vendor];
  }, [input.vendor]);
  const RenderRaw = useMemo(() => {
    return VendorRenderRaw[input.vendor];
  }, [input.vendor]);
  const checkIsDataWithError = useCallback((data: unknown) => {
    return isType(ErrorRegistryInformationSchema)(data);
  }, []);
  const error = useMemo(() => {
    if (checkIsDataWithError(input.data)) {
      return input.data.error || 'Registry data unavailable';
    }

    return;
  }, [input.data, checkIsDataWithError]);

  return (
    <ReportWrapper
      title={
        <div className="flex items-center gap-2">
          <Icon className="h-6 w-6" />
          <span>Registry Data</span>
        </div>
      }
      Icon={Database}
      tooltip="Official company registry information from company registries."
      showTooltip={false}
    >
      <RegistryDataDisplay {...adaptedData} renderRaw={RenderRaw} error={error} />
    </ReportWrapper>
  );
};
